import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useState, useRef, useEffect } from 'react'
import {
  PrivacyState,
  type WritableAccessMetadata,
  type ReadableAccessMetadata,
  type UserLike,
  type InheritanceResource,
  EffectivePrivacy,
} from '@opallabs/concepts'
import { type Theme, ZIndex } from '@opallabs/gem'
import { useWorkspace } from '@opallabs/hooks'
import {
  Button,
  Icon,
  SingleSelect,
  InputLabel,
  ButtonIconPosition,
  Tooltip,
  Modal,
  DotLoader,
} from '@opallabs/lapidary'
import { CollaboratorPermissionList } from '../../CollaboratorPermissionList'
import { PermissionsTextSummary } from '../../PermissionsTextSummary'
import type {
  SupportedResource,
  PermUserLike,
  ResourceMeta,
  ResourceShareParams,
} from '../../types'
import { useEditableAccessMembers } from '../../useEditableAccessMembers'
import * as styles from '../styles'
import { useSharingSaveState } from '../useSharingSaveState'

type ResourceShareBodyProps<ResourceType extends SupportedResource> = {
  accessMetadata?: ReadableAccessMetadata
  className?: string
  effectivePrivacy: EffectivePrivacy | null
  /**
   * The `<Modal>` that renders this conditionally *also* renders the `<ConfirmationBody>` child component without
   * removing this one. Originally this worked ok because the `<Modal.Title>`s and `<Modal.Footer>`s were rendered
   * inside `<Modal.Body>`, so there was a single DOM node that could be set to `display: none` in this component. Now
   * that the title and footer are unnested we need an alternative approach; this was the quickest option, but there’s
   * likely a better option.
   */
  hideContent?: boolean
  onChangeAccess: (access: WritableAccessMetadata) => Promise<void>
  onClose: ({ changesSaved }: { changesSaved: boolean }) => void
  ownerId?: string
  readOnly: boolean
  resourceId: string
  resourceMeta: ResourceMeta<ResourceType>
  resourceShareParams?: Omit<
    ResourceShareParams<ResourceType>,
    'workspace' | 'resourceId'
  >
  resourceTitle: string
  resourceDisplayType?: string
  setCanEscapeClose: (canClose: boolean) => void
  theme: Theme
}

export const ResourceShareBody = <ResourceType extends SupportedResource>({
  accessMetadata,
  className,
  effectivePrivacy,
  hideContent = false,
  ownerId,
  onChangeAccess,
  onClose,
  readOnly,
  resourceId,
  resourceMeta,
  resourceShareParams,
  resourceTitle,
  resourceDisplayType = resourceMeta.name,
  setCanEscapeClose,
  theme,
}: ResourceShareBodyProps<ResourceType>) => {
  const currentWorkspace = useWorkspace()
  const {
    collaborators,
    currentUser,
    isLoading,
    owner,
    initialCollaborators,
    inheritanceResources,
    memberCount,
    spacesCount,
    workspaceResource,
    addCollaborator,
    removeCollaborator,
    setInheritance,
  } = useEditableAccessMembers({ accessMetadata, ownerId })

  const canChangePrivacy = Boolean(
    currentUser &&
      ((owner && currentUser.uuid === owner.uuid) ||
        currentUser.isAdminForCurrentWorkspace)
  )

  const membershipInheritedFromWorkspace = Boolean(
    workspaceResource?.inheritsMembership
  )

  const canSaveChanges =
    !readOnly && (!membershipInheritedFromWorkspace || canChangePrivacy)
  const [isSaving, setIsSaving] = useState(false)
  const [hasCopiedLink, setHasCopiedLink] = useState(false)
  const copyButtonRef = useRef<HTMLButtonElement | null>(null)

  const {
    privacyState,
    hasUnsavedChanges,
    onAddCollaborator,
    onRemoveCollaborator,
    onChangeInheritance,
    onSave,
    onSelectPrivacy,
    privacySelectOptions,
    selectedPrivacyOption,
  } = useSharingSaveState({
    accessMetadata,
    onChangeAccess,
    ownerId,
    membershipInheritedFromWorkspace,
  })

  const handleAddCollaborator = (collaborator: UserLike) => {
    addCollaborator(collaborator)
    onAddCollaborator(collaborator)
  }

  const handleRemoveCollaborator = (
    collaborator: UserLike,
    focusOutsideElement?: boolean
  ) => {
    if (focusOutsideElement) {
      copyButtonRef.current?.focus()
    }
    removeCollaborator(collaborator)
    onRemoveCollaborator(collaborator)
  }

  const handleChangeInheritance = (
    inheritanceResource: InheritanceResource,
    inheritsMembership: boolean
  ) => {
    setInheritance(inheritanceResource, inheritsMembership)
    onChangeInheritance(inheritanceResource, inheritsMembership)
  }

  const handleSelectPrivacy = (privacy: PrivacyState) => {
    // A non-owner (e.g. a workspace admin) making the resource private would
    // lock themselves out — keep them in the collaborator list.
    if (
      privacy === PrivacyState.Private &&
      currentUser &&
      currentUser.uuid !== ownerId &&
      !collaborators.some(({ uuid }) => uuid === currentUser.uuid)
    ) {
      handleAddCollaborator({
        type: 'user',
        uuid: currentUser.uuid,
        legacyId: currentUser.id,
        givenName: currentUser.firstName,
        familyName: currentUser.lastName,
        name: currentUser.fullName,
        activeWorkspaceMember: true,
      })
    }
    onSelectPrivacy(privacy)
  }

  const handleCopyLink = () => {
    const shareUrl = resourceMeta.getShareUrl({
      workspace: currentWorkspace,
      resourceId,
      ...resourceShareParams,
    })
    setHasCopiedLink(true)
    navigator.clipboard.writeText(shareUrl)
    setTimeout(() => setHasCopiedLink(false), 2000)
  }

  useEffect(() => {
    setCanEscapeClose(!hasUnsavedChanges)
  }, [hasUnsavedChanges, setCanEscapeClose])

  return (
    <>
      <Modal.Title data-hideform={hideContent}>
        Share {resourceDisplayType}
      </Modal.Title>
      <Modal.Body className={className} data-hideform={hideContent}>
        <VisuallyHidden asChild>
          <Modal.Description>
            Manage collaborators on &ldquo;{resourceTitle}&rdquo;.
          </Modal.Description>
        </VisuallyHidden>
        <div css={styles.resourceTitleContainer(theme)}>
          <Icon
            glyph={resourceMeta.glyph(theme, resourceDisplayType)}
            height={24}
            width={24}
            ariaHidden
          />
          <p css={styles.resourceTitle(theme)}>{resourceTitle}</p>
        </div>
        {isLoading ? (
          <div
            css={styles.loaderContainer}
            style={{
              minBlockSize:
                effectivePrivacy === EffectivePrivacy.EffectivelyPrivate
                  ? 480
                  : 296,
            }}
          >
            <DotLoader />
          </div>
        ) : (
          <>
            {Boolean(workspaceResource) && (
              <div css={styles.privacySelect}>
                <InputLabel css={styles.label} text="Privacy" />

                <SingleSelect.Root
                  options={privacySelectOptions}
                  initialSelection={selectedPrivacyOption}
                  onChange={(option) =>
                    option && handleSelectPrivacy(option.value as PrivacyState)
                  }
                >
                  <SingleSelect.Body>
                    {canChangePrivacy ? (
                      <SingleSelect.Trigger>
                        <SingleSelect.TriggerText />
                      </SingleSelect.Trigger>
                    ) : (
                      <Tooltip
                        content="Only the owner or workspace admins can change privacy"
                        zIndex={ZIndex.Shroud.Tooltip}
                      >
                        <SingleSelect.Trigger disabled>
                          <SingleSelect.TriggerText />
                        </SingleSelect.Trigger>
                      </Tooltip>
                    )}
                    <SingleSelect.Menu>
                      <SingleSelect.MenuItems />
                    </SingleSelect.Menu>
                  </SingleSelect.Body>
                </SingleSelect.Root>
              </div>
            )}
            <div css={styles.permissionListWrapper}>
              <CollaboratorPermissionList<PermUserLike>
                currentUserUuid={currentUser?.uuid ?? ''}
                hideCollaborators={privacyState === PrivacyState.Public}
                inheritanceResources={inheritanceResources}
                initialCollaborators={initialCollaborators}
                inputLabel="Collaborators"
                onAddCollaborator={handleAddCollaborator}
                onChangeInheritance={handleChangeInheritance}
                onRemoveCollaborator={handleRemoveCollaborator}
                owner={owner}
                readOnly={readOnly}
                restrictToWorkspace={['user', 'user_group']}
                searchPlaceholder="Add users or groups…"
                showUserGroups
                updateInitialValueOnPropChange={true}
                userCanRemoveSelf={false}
                width="auto"
              />
              {accessMetadata && (
                <PermissionsTextSummary
                  css={styles.permissionSummary}
                  effectivePrivacy={effectivePrivacy}
                  memberCount={memberCount}
                  spaceCount={spacesCount}
                  privacyState={privacyState}
                  resourceDisplayType={resourceDisplayType.toLocaleLowerCase()}
                />
              )}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer css={styles.footerOverrides} data-hideform={hideContent}>
        <Tooltip
          disabled={privacyState === PrivacyState.Public}
          content={`Only collaborators can view this ${resourceDisplayType.toLocaleLowerCase()}`}
          zIndex={ZIndex.Shroud.Tooltip}
        >
          <Button
            className="copy-resource-link"
            ref={copyButtonRef}
            buttonText={hasCopiedLink ? 'Copied!' : 'Copy Link'}
            variant="tertiary"
            icon={
              <Icon
                glyph={hasCopiedLink ? 'gem-check-circle' : 'gem-link'}
                ariaHidden
              />
            }
            iconPosition={ButtonIconPosition.leading}
            name="Copy resource link"
            onClick={handleCopyLink}
            styleOverrides={{ button: styles.buttonOverrides }}
          />
        </Tooltip>
        <Button
          name="Save changes"
          disabled={!canSaveChanges || !hasUnsavedChanges || isSaving}
          variant="primary"
          onClick={async () => {
            setIsSaving(true)
            await onSave()
            setIsSaving(false)
            onClose({ changesSaved: true })
          }}
          buttonText="Save"
        />
      </Modal.Footer>
    </>
  )
}
